import React, { useState, useEffect } from 'react';
import { Routes, Route, Link, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { timeOffRequestsAPI, timeOffBalancesAPI } from '../../services/api';
import { useTheme } from '../../context/ThemeContext';
import { useToast } from '../../context/ToastContext';
import { useConfirm } from '../../context/ConfirmContext';
import TimeOffCalendarView from './TimeOffCalendarView';
import {
  PlusIcon,
  CalendarIcon,
  ArrowLeftIcon,
  SearchIcon,
  CheckIcon,
  XIcon,
  MailIcon,
  ClockIcon,
  ChevronRightIcon,
  EditIcon,
  TrashIcon,
} from '../icons';

// Premium line icons (1.5px stroke weight)
const Icons = {
  plus: <PlusIcon className="" width={18} height={18} strokeWidth={1.5} />,
  calendar: <CalendarIcon className="" width={20} height={20} strokeWidth={1.5} />,
  arrowLeft: <ArrowLeftIcon className="" width={18} height={18} strokeWidth={1.5} />,
  search: <SearchIcon className="" width={18} height={18} strokeWidth={1.5} />,
  check: <CheckIcon className="" width={16} height={16} strokeWidth={1.5} />,
  x: <XIcon className="" width={16} height={16} strokeWidth={1.5} />,
  mail: <MailIcon className="" width={14} height={14} strokeWidth={1.5} />,
  clock: <ClockIcon className="" width={14} height={14} strokeWidth={1.5} />,
  chevronRight: <ChevronRightIcon className="" width={18} height={18} strokeWidth={1.5} />,
  edit: <EditIcon className="" width={16} height={16} strokeWidth={1.5} />,
  trash: <TrashIcon className="" width={16} height={16} strokeWidth={1.5} />,
};

interface TimeOffRequest {
  id: number;
  user: {
    id: number;
    full_name: string;
    email: string;
  };
  request_type: string;
  start_date: string;
  end_date: string;
  total_days: number;
  hours_requested: number;
  reason: string;
  status: string;
  approved_by?: {
    id: number;
    full_name: string;
    email: string;
  } | null;
  approved_at?: string;
  created_at: string;
}

interface DateRangePickerProps {
  startDate: string;
  endDate: string;
  onDateChange: (startDate: string, endDate: string) => void;
  isDark: boolean;
}

const DateRangePicker: React.FC<DateRangePickerProps> = ({
  startDate,
  endDate,
  onDateChange,
  isDark,
}) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [selectingStart, setSelectingStart] = useState(true);
  const [tempStartDate, setTempStartDate] = useState(startDate);
  const [tempEndDate, setTempEndDate] = useState(endDate);

  useEffect(() => {
    setTempStartDate(startDate);
    setTempEndDate(endDate);
  }, [startDate, endDate]);

  const formatDisplayText = () => {
    if (!startDate && !endDate) return t('timeOff.selectDateRange');
    if (startDate && !endDate)
      return `${new Date(startDate).toLocaleDateString()} - ${t('timeOff.selectEndDate')}`;
    if (startDate && endDate) {
      if (startDate === endDate) {
        return new Date(startDate).toLocaleDateString();
      }
      return `${new Date(startDate).toLocaleDateString()} - ${new Date(endDate).toLocaleDateString()}`;
    }
    return t('timeOff.selectDateRange');
  };

  const handleDateClick = (date: string) => {
    if (selectingStart) {
      setTempStartDate(date);
      setTempEndDate('');
      setSelectingStart(false);
    } else {
      if (date >= tempStartDate) {
        setTempEndDate(date);
        onDateChange(tempStartDate, date);
        setIsOpen(false);
        setSelectingStart(true);
      } else {
        setTempStartDate(date);
        setTempEndDate('');
      }
    }
  };

  const generateCalendarDays = () => {
    const today = new Date();
    const currentMonth = new Date(today.getFullYear(), today.getMonth(), 1);
    const nextMonth = new Date(today.getFullYear(), today.getMonth() + 1, 1);

    const days = [];

    for (let month of [currentMonth, nextMonth]) {
      const daysInMonth = new Date(
        month.getFullYear(),
        month.getMonth() + 1,
        0,
      ).getDate();
      const monthDays = [];

      for (let day = 1; day <= daysInMonth; day++) {
        const date = new Date(month.getFullYear(), month.getMonth(), day);
        const dateString = date.toISOString().split('T')[0];
        const isToday = dateString === today.toISOString().split('T')[0];
        const isSelected =
          dateString === tempStartDate || dateString === tempEndDate;
        const isInRange =
          tempStartDate &&
          tempEndDate &&
          dateString >= tempStartDate &&
          dateString <= tempEndDate;
        const isPast = date < today;

        monthDays.push({
          date: dateString,
          day: day,
          isToday,
          isSelected,
          isInRange,
          isPast,
        });
      }

      days.push({
        month: month.toLocaleDateString('en-US', {
          month: 'long',
          year: 'numeric',
        }),
        days: monthDays,
      });
    }

    return days;
  };

  return (
    <div className="relative">
      <div
        onClick={() => setIsOpen(!isOpen)}
        className={`w-full px-4 py-2.5 border rounded-xl cursor-pointer flex items-center justify-between transition-colors ${
          isDark
            ? 'bg-slate-900/50 border-slate-700 text-white hover:border-violet-500'
            : 'bg-white border-slate-200 text-slate-900 hover:border-violet-500'
        }`}
      >
        <span
          className={
            !startDate && !endDate
              ? isDark
                ? 'text-slate-500'
                : 'text-slate-400'
              : ''
          }
        >
          {formatDisplayText()}
        </span>
        <div className="flex items-center gap-2">
          {selectingStart && isOpen && (
            <span className="text-xs text-violet-400">
              {t('timeOff.selectStartDate')}
            </span>
          )}
          {!selectingStart && isOpen && (
            <span className="text-xs text-violet-400">
              {t('timeOff.selectEndDate')}
            </span>
          )}
          <span className={isDark ? 'text-slate-500' : 'text-slate-400'}>
            {Icons.calendar}
          </span>
        </div>
      </div>

      {isOpen && (
        <div
          className={`absolute top-full left-0 mt-2 rounded-2xl shadow-2xl z-50 p-4 w-full max-w-[600px] overflow-x-auto ${isDark ? 'bg-slate-900 border border-slate-700' : 'bg-white border border-slate-200'}`}
        >
          <div className="flex gap-4">
            {generateCalendarDays().map((monthData, monthIndex) => (
              <div key={monthIndex} className="flex-1">
                <h3
                  className={`font-semibold mb-3 text-center ${isDark ? 'text-white' : 'text-slate-900'}`}
                >
                  {monthData.month}
                </h3>
                <div className="grid grid-cols-7 gap-1 text-xs">
                  {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map((day) => (
                    <div
                      key={day}
                      className={`text-center font-medium py-2 ${isDark ? 'text-slate-500' : 'text-slate-500'}`}
                    >
                      {day}
                    </div>
                  ))}

                  {Array.from(
                    { length: new Date(monthData.days[0].date).getDay() },
                    (_, i) => (
                      <div key={`empty-${i}`} className="py-2"></div>
                    ),
                  )}

                  {monthData.days.map((dayData) => (
                    <button
                      key={dayData.date}
                      onClick={() => handleDateClick(dayData.date)}
                      disabled={dayData.isPast}
                      className={`
                        py-2 px-1 text-center rounded-lg transition-colors
                        ${dayData.isPast ? (isDark ? 'text-slate-600' : 'text-slate-300') + ' cursor-not-allowed' : 'cursor-pointer'}
                        ${dayData.isSelected ? 'bg-violet-600 text-white' : ''}
                        ${dayData.isInRange && !dayData.isSelected ? (isDark ? 'bg-violet-500/20' : 'bg-violet-100') : ''}
                        ${dayData.isToday && !dayData.isSelected ? (isDark ? 'bg-slate-700 font-semibold' : 'bg-slate-200 font-semibold') : ''}
                        ${!dayData.isPast && !dayData.isSelected && !dayData.isInRange && !dayData.isToday ? (isDark ? 'hover:bg-slate-700 text-white' : 'hover:bg-slate-100 text-slate-900') : ''}
                      `}
                    >
                      {dayData.day}
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>

          <div
            className={`mt-4 flex justify-between items-center pt-4 border-t ${isDark ? 'border-slate-700' : 'border-slate-200'}`}
          >
            <div
              className={`text-sm ${isDark ? 'text-slate-400' : 'text-slate-600'}`}
            >
              {selectingStart
                ? t('timeOff.clickSelectStartDate')
                : t('timeOff.clickSelectEndDate')}
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => {
                  setTempStartDate('');
                  setTempEndDate('');
                  onDateChange('', '');
                  setSelectingStart(true);
                }}
                className={`px-3 py-1.5 text-sm rounded-lg transition-colors ${isDark ? 'bg-slate-700 hover:bg-slate-600 text-white' : 'bg-slate-100 hover:bg-slate-200 text-slate-700'}`}
              >
                {t('common.clear')}
              </button>
              <button
                onClick={() => {
                  setIsOpen(false);
                  setSelectingStart(true);
                }}
                className="px-3 py-1.5 text-sm bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-500 hover:to-purple-500 text-white rounded-lg"
              >
                {t('common.close')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

interface RejectModalProps {
  request: TimeOffRequest;
  isDark: boolean;
  onCancel: () => void;
  onConfirm: (reason: string) => void;
}

const RejectModal: React.FC<RejectModalProps> = ({
  request,
  isDark,
  onCancel,
  onConfirm,
}) => {
  const { t } = useTranslation();
  const [reason, setReason] = useState('');

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={onCancel}
      />
      <div
        className={`relative w-full max-w-md rounded-2xl shadow-2xl p-6 ${
          isDark
            ? 'bg-slate-900 border border-slate-700'
            : 'bg-white border border-slate-200'
        }`}
      >
        {/* Title */}
        <h2
          className={`text-lg font-bold mb-1 ${isDark ? 'text-white' : 'text-slate-900'}`}
        >
          {t('timeOff.rejectTitle')}
        </h2>

        {/* Request summary */}
        <div
          className={`mb-4 rounded-xl p-3 text-sm ${isDark ? 'bg-slate-800 text-slate-300' : 'bg-slate-50 text-slate-700'}`}
        >
          <div className="font-semibold mb-0.5">{request.user.full_name}</div>
          <div>
            {t(`timeOff.${request.request_type}`)} &mdash;{' '}
            {new Date(request.start_date).toLocaleDateString()} &ndash;{' '}
            {new Date(request.end_date).toLocaleDateString()}
          </div>
        </div>

        {/* Reason textarea */}
        <label
          className={`block text-sm font-medium mb-1.5 ${isDark ? 'text-slate-300' : 'text-slate-700'}`}
        >
          {t('timeOff.rejectionReason')}{' '}
          <span className="text-rose-500">*</span>
        </label>
        <textarea
          autoFocus
          rows={3}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder={t('timeOff.rejectionReasonPlaceholder')}
          className={`w-full px-4 py-2.5 rounded-xl border text-sm resize-none transition-colors focus:outline-none focus:ring-1 ${
            isDark
              ? 'bg-slate-800 border-slate-700 text-white placeholder-slate-500 focus:border-violet-500 focus:ring-violet-500'
              : 'bg-white border-slate-200 text-slate-900 placeholder-slate-400 focus:border-violet-500 focus:ring-violet-500'
          }`}
        />

        {/* Buttons */}
        <div className="flex justify-end gap-3 mt-4">
          <button
            onClick={onCancel}
            className={`px-4 py-2 text-sm font-medium rounded-xl transition-colors ${
              isDark
                ? 'bg-slate-700 hover:bg-slate-600 text-white'
                : 'bg-slate-100 hover:bg-slate-200 text-slate-700'
            }`}
          >
            {t('common.cancel')}
          </button>
          <button
            onClick={() => onConfirm(reason)}
            disabled={reason.trim().length === 0}
            className={`px-4 py-2 text-sm font-semibold rounded-xl transition-colors ${
              reason.trim().length === 0
                ? 'bg-rose-300 text-white cursor-not-allowed opacity-60'
                : 'bg-rose-600 hover:bg-rose-500 text-white'
            }`}
          >
            {t('timeOff.reject')}
          </button>
        </div>
      </div>
    </div>
  );
};

const TimeOffList: React.FC = () => {
  const { t } = useTranslation();
  const { theme } = useTheme();
  const { showToast } = useToast();
  const { confirm } = useConfirm();
  const isDark = theme === 'dark';
  const [requests, setRequests] = useState<TimeOffRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [userFilter, setUserFilter] = useState('');
  const [expandedRequest, setExpandedRequest] = useState<number | null>(null);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [rejectModalRequest, setRejectModalRequest] =
    useState<TimeOffRequest | null>(null);
  const [viewMode, setViewMode] = useState<'list' | 'calendar' | 'expenses'>(
    'list',
  );

  // Theme-aware classes
  const cardClass = isDark
    ? 'bg-slate-900/50 border border-slate-700/50 backdrop-blur-sm'
    : 'bg-white border border-slate-200 shadow-sm';

  const inputClass = isDark
    ? 'w-full px-4 py-2.5 bg-slate-900/50 border border-slate-700 text-white placeholder-slate-500 rounded-xl focus:border-violet-500 focus:ring-1 focus:ring-violet-500 transition-colors'
    : 'w-full px-4 py-2.5 bg-white border border-slate-200 text-slate-900 placeholder-slate-400 rounded-xl focus:border-violet-500 focus:ring-1 focus:ring-violet-500 transition-colors';

  const selectClass = isDark
    ? 'px-4 py-2.5 bg-slate-900/50 border border-slate-700 text-white rounded-xl focus:border-violet-500 focus:ring-1 focus:ring-violet-500 transition-colors'
    : 'px-4 py-2.5 bg-white border border-slate-200 text-slate-900 rounded-xl focus:border-violet-500 focus:ring-1 focus:ring-violet-500 transition-colors';

  useEffect(() => {
    fetchRequests();
  }, []);

  const fetchRequests = async () => {
    try {
      const response = await timeOffRequestsAPI.getAll();
      setRequests(response.data.time_off_requests || []);
    } catch (error) {
      console.error('Failed to fetch time off requests:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (id: number) => {
    const confirmed = await confirm({
      title: t('timeOff.approveTitle'),
      message: t('timeOff.approveConfirm'),
      confirmLabel: t('timeOff.approve'),
      cancelLabel: t('common.cancel'),
      variant: 'info',
    });
    if (!confirmed) return;
    try {
      await timeOffRequestsAPI.approve(id);
      showToast(t('timeOff.approveSuccess'), 'success');
      fetchRequests();
    } catch (error) {
      console.error('Failed to approve request:', error);
      showToast(t('timeOff.approveFailed'), 'error');
    }
  };

  const handleReject = (request: TimeOffRequest) => {
    setRejectModalRequest(request);
  };

  const handleRejectConfirm = async (reason: string) => {
    if (!rejectModalRequest) return;
    const id = rejectModalRequest.id;
    setRejectModalRequest(null);
    try {
      await timeOffRequestsAPI.reject(id, reason);
      showToast(t('timeOff.rejectSuccess'), 'success');
      fetchRequests();
    } catch (error) {
      console.error('Failed to reject request:', error);
      showToast(t('timeOff.rejectFailed'), 'error');
    }
  };

  const handleDelete = async (id: number) => {
    const confirmed = await confirm({
      title: t('timeOff.deleteTitle'),
      message: t('timeOff.deleteConfirm'),
      confirmLabel: t('common.delete'),
      cancelLabel: t('common.cancel'),
      variant: 'danger',
    });
    if (!confirmed) return;
    try {
      await timeOffRequestsAPI.delete(id);
      fetchRequests();
    } catch (error) {
      console.error('Failed to delete request:', error);
      showToast(t('timeOff.deleteFailed'), 'error');
    }
  };

  const filteredRequests = requests
    .filter((request) => {
      const matchesStatus = !statusFilter || request.status === statusFilter;
      const matchesType = !typeFilter || request.request_type === typeFilter;
      const matchesUser =
        !userFilter ||
        request.user.full_name.toLowerCase().includes(userFilter.toLowerCase());

      return matchesStatus && matchesType && matchesUser;
    })
    .sort((a, b) => {
      // Pending first, then rejected, then approved, then by date descending
      const statusOrder: Record<string, number> = {
        pending: 0,
        rejected: 1,
        approved: 2,
      };
      const aOrder = statusOrder[a.status] ?? 3;
      const bOrder = statusOrder[b.status] ?? 3;
      if (aOrder !== bOrder) return aOrder - bOrder;
      return (
        new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
      );
    });

  const getStatusColor = (status: string) => {
    if (isDark) {
      switch (status) {
        case 'pending':
          return 'bg-amber-500/20 text-amber-400 border border-amber-500/30';
        case 'approved':
          return 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30';
        case 'rejected':
          return 'bg-rose-500/20 text-rose-400 border border-rose-500/30';
        default:
          return 'bg-slate-500/20 text-slate-400 border border-slate-500/30';
      }
    } else {
      switch (status) {
        case 'pending':
          return 'bg-amber-50 text-amber-700 border border-amber-200';
        case 'approved':
          return 'bg-emerald-50 text-emerald-700 border border-emerald-200';
        case 'rejected':
          return 'bg-rose-50 text-rose-700 border border-rose-200';
        default:
          return 'bg-slate-100 text-slate-700 border border-slate-200';
      }
    }
  };

  const getTypeColor = (type: string) => {
    if (isDark) {
      switch (type) {
        case 'vacation':
          return 'bg-cyan-500/20 text-cyan-400 border border-cyan-500/30';
        case 'sick':
        case 'sick_leave':
          return 'bg-rose-500/20 text-rose-400 border border-rose-500/30';
        case 'personal':
          return 'bg-violet-500/20 text-violet-400 border border-violet-500/30';
        case 'emergency':
          return 'bg-orange-500/20 text-orange-400 border border-orange-500/30';
        default:
          return 'bg-slate-500/20 text-slate-400 border border-slate-500/30';
      }
    } else {
      switch (type) {
        case 'vacation':
          return 'bg-cyan-50 text-cyan-700 border border-cyan-200';
        case 'sick':
        case 'sick_leave':
          return 'bg-rose-50 text-rose-700 border border-rose-200';
        case 'personal':
          return 'bg-violet-50 text-violet-700 border border-violet-200';
        case 'emergency':
          return 'bg-orange-50 text-orange-700 border border-orange-200';
        default:
          return 'bg-slate-100 text-slate-700 border border-slate-200';
      }
    }
  };

  if (loading) {
    return (
      <div
        className={`min-h-screen p-6 ${isDark ? 'bg-slate-900' : 'bg-slate-50'}`}
      >
        <div className="animate-pulse">
          <div
            className={`h-10 ${isDark ? 'bg-slate-700' : 'bg-slate-200'} rounded-xl w-1/4 mb-6`}
          ></div>
          <div className="space-y-4">
            {[1, 2, 3].map((i) => (
              <div
                key={i}
                className={`h-32 ${isDark ? 'bg-slate-900' : 'bg-slate-200'} rounded-2xl`}
              ></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      {rejectModalRequest && (
        <RejectModal
          request={rejectModalRequest}
          isDark={isDark}
          onCancel={() => setRejectModalRequest(null)}
          onConfirm={handleRejectConfirm}
        />
      )}
      <div
        className={`min-h-screen p-6 ${isDark ? 'bg-slate-900' : 'bg-slate-50'}`}
      >
        {/* Sticky Header */}
        <div
          className={`sticky top-0 z-20 ${isDark ? 'bg-slate-900' : 'bg-slate-50'} pb-4 -mx-6 px-6 pt-0`}
        >
          {/* Header */}
          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 mb-4">
            <div>
              <div className="flex items-center gap-3 mb-1">
                <div
                  className={`hidden sm:flex p-2 rounded-xl ${isDark ? 'bg-violet-500/20' : 'bg-violet-100'}`}
                >
                  <span
                    className={isDark ? 'text-violet-400' : 'text-violet-600'}
                  >
                    {Icons.calendar}
                  </span>
                </div>
                <h1
                  className={`text-xl sm:text-2xl font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}
                >
                  {t('timeOff.title')}
                </h1>
              </div>
              <p
                className={`text-sm ${isDark ? 'text-slate-400' : 'text-slate-600'} sm:ml-14`}
              >
                {requests.filter((r) => r.status === 'pending').length}{' '}
                {t('timeOff.pendingRequests')}
              </p>
            </div>
            <div className="flex items-center gap-2">
              <Link
                to="/manager/time-off/report"
                className={`inline-flex items-center gap-2 px-4 py-2.5 text-sm font-semibold rounded-xl border transition-all duration-200 ${
                  isDark
                    ? 'border-slate-700 text-slate-300 hover:bg-slate-800'
                    : 'border-slate-200 text-slate-700 hover:bg-slate-100'
                }`}
              >
                {Icons.calendar}
                {t('timeOff.report', 'Report')}
              </Link>
              <Link
                to="/manager/time-off/new"
                className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-5 py-2.5 bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-500 hover:to-purple-500 text-white text-sm font-semibold rounded-xl shadow-lg shadow-violet-500/25 transition-all duration-200"
              >
                {Icons.plus}
                {t('timeOff.requestTimeOff')}
              </Link>
            </div>
          </div>

          {/* Filters */}
          <div className={`${cardClass} rounded-2xl p-4`}>
            {/* Search always visible */}
            <div className="relative mb-4 md:mb-0 md:hidden">
              <span
                className={`absolute left-4 top-1/2 -translate-y-1/2 ${isDark ? 'text-slate-500' : 'text-slate-400'}`}
              >
                {Icons.search}
              </span>
              <input
                type="text"
                placeholder={t('timeOff.searchByUser')}
                value={userFilter}
                onChange={(e) => setUserFilter(e.target.value)}
                className={`${inputClass} pl-11`}
              />
            </div>

            {/* Mobile filter toggle */}
            <button
              onClick={() => setFiltersOpen(!filtersOpen)}
              className={`md:hidden w-full flex items-center justify-between py-2 ${isDark ? 'text-slate-300' : 'text-slate-700'}`}
            >
              <span className="flex items-center gap-2 text-sm font-medium">
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"></polygon>
                </svg>
                {t('common.filters')}
                {(statusFilter || typeFilter) && (
                  <span className="px-1.5 py-0.5 text-xs rounded-full bg-violet-500 text-white">
                    {[statusFilter, typeFilter].filter(Boolean).length}
                  </span>
                )}
              </span>
              <svg
                className={`w-5 h-5 transition-transform ${filtersOpen ? 'rotate-180' : ''}`}
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <polyline points="6 9 12 15 18 9"></polyline>
              </svg>
            </button>

            {/* Filters - collapsible on mobile */}
            <div className={`${filtersOpen ? 'block' : 'hidden'} md:block`}>
              <div className="flex flex-col md:flex-row gap-4 mt-4 md:mt-0">
                {/* Search - hidden on mobile (shown above) */}
                <div className="hidden md:block flex-1 min-w-64 relative">
                  <span
                    className={`absolute left-4 top-1/2 -translate-y-1/2 ${isDark ? 'text-slate-500' : 'text-slate-400'}`}
                  >
                    {Icons.search}
                  </span>
                  <input
                    type="text"
                    placeholder={t('timeOff.searchByUser')}
                    value={userFilter}
                    onChange={(e) => setUserFilter(e.target.value)}
                    className={`${inputClass} pl-11`}
                  />
                </div>
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className={selectClass}
                >
                  <option value="">{t('timeOff.allStatus')}</option>
                  <option value="pending">{t('timeEntry.pending')}</option>
                  <option value="approved">{t('timeEntry.approved')}</option>
                  <option value="rejected">{t('timeEntry.rejected')}</option>
                </select>
                <select
                  value={typeFilter}
                  onChange={(e) => setTypeFilter(e.target.value)}
                  className={selectClass}
                >
                  <option value="">{t('timeOff.allTypes')}</option>
                  <option value="vacation">{t('timeOff.vacation')}</option>
                  <option value="sick_leave">{t('timeOff.sickLeave')}</option>
                  <option value="personal">{t('timeOff.personal')}</option>
                  <option value="emergency">{t('timeOff.emergency')}</option>
                </select>
              </div>
            </div>
          </div>
        </div>

        {/* View Mode Tabs */}
        <div
          className={`flex gap-1 mb-4 p-1 rounded-xl w-fit ${isDark ? 'bg-slate-800' : 'bg-slate-100'}`}
        >
          <button
            onClick={() => setViewMode('list')}
            className={`px-4 py-2 text-sm font-medium rounded-lg transition-all ${
              viewMode === 'list'
                ? isDark
                  ? 'bg-slate-700 text-white shadow'
                  : 'bg-white text-slate-900 shadow'
                : isDark
                  ? 'text-slate-400 hover:text-slate-200'
                  : 'text-slate-500 hover:text-slate-700'
            }`}
          >
            {t('timeOff.listView', 'List View')}
          </button>
          <button
            onClick={() => setViewMode('calendar')}
            className={`px-4 py-2 text-sm font-medium rounded-lg transition-all ${
              viewMode === 'calendar'
                ? isDark
                  ? 'bg-slate-700 text-white shadow'
                  : 'bg-white text-slate-900 shadow'
                : isDark
                  ? 'text-slate-400 hover:text-slate-200'
                  : 'text-slate-500 hover:text-slate-700'
            }`}
          >
            {t('timeOff.calendarView', 'Calendar View')}
          </button>
        </div>

        {/* Calendar View */}
        {viewMode === 'calendar' && (
          <TimeOffCalendarView
            isDark={isDark}
            cardClass={cardClass}
            arrowLeftIcon={Icons.arrowLeft}
            chevronRightIcon={Icons.chevronRight}
          />
        )}

        {/* Requests List - Accordion */}
        {viewMode === 'list' && (
          <div className="space-y-3">
            {filteredRequests.length === 0 ? (
              <div
                className={`${cardClass} rounded-2xl p-12 text-center ${isDark ? 'text-slate-400' : 'text-slate-500'}`}
              >
                <div
                  className={`w-16 h-16 mx-auto mb-4 rounded-2xl ${isDark ? 'bg-slate-900' : 'bg-slate-100'} flex items-center justify-center`}
                >
                  <span
                    className={isDark ? 'text-slate-600' : 'text-slate-400'}
                  >
                    {Icons.calendar}
                  </span>
                </div>
                <p className="font-medium">{t('timeOff.noRequests')}</p>
              </div>
            ) : (
              filteredRequests.map((request) => {
                const isExpanded = expandedRequest === request.id;

                return (
                  <div
                    key={request.id}
                    className={`${cardClass} rounded-2xl overflow-hidden`}
                  >
                    {/* Accordion Header - Clickable */}
                    <button
                      onClick={() =>
                        setExpandedRequest(isExpanded ? null : request.id)
                      }
                      className={`w-full px-4 sm:px-6 py-4 ${isDark ? 'hover:bg-slate-700/30' : 'hover:bg-slate-50'} transition-colors`}
                    >
                      <div className="flex items-start sm:items-center gap-2 sm:gap-3">
                        <span
                          className={`mt-1 sm:mt-0 transition-transform duration-200 flex-shrink-0 ${isExpanded ? 'rotate-90' : ''} ${isDark ? 'text-slate-400' : 'text-slate-500'}`}
                        >
                          {Icons.chevronRight}
                        </span>
                        <div className="hidden sm:flex flex-shrink-0 h-10 w-10 bg-gradient-to-br from-violet-500 to-purple-600 rounded-xl items-center justify-center">
                          <span className="text-sm font-bold text-white">
                            {request.user.full_name
                              .split(' ')
                              .map((n) => n[0])
                              .join('')
                              .substring(0, 2)}
                          </span>
                        </div>
                        <div className="flex-1 min-w-0 text-left">
                          <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2">
                            <h3
                              className={`text-sm font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}
                            >
                              {request.user.full_name}
                            </h3>
                            <div className="flex flex-wrap gap-1">
                              <span
                                className={`inline-flex px-2 py-0.5 text-xs font-medium rounded-lg ${getTypeColor(request.request_type)}`}
                              >
                                {t(`timeOff.${request.request_type}`)}
                              </span>
                              <span
                                className={`inline-flex px-2 py-0.5 text-xs font-medium rounded-lg ${getStatusColor(request.status)}`}
                              >
                                {t(`timeEntry.${request.status}`)}
                              </span>
                            </div>
                          </div>
                          <p
                            className={`text-xs text-left mt-1 ${isDark ? 'text-slate-400' : 'text-slate-500'}`}
                          >
                            {new Date(request.start_date).toLocaleDateString()}{' '}
                            - {new Date(request.end_date).toLocaleDateString()}{' '}
                            • {request.total_days}{' '}
                            {request.total_days !== 1
                              ? t('timeOff.days')
                              : t('timeOff.day')}
                          </p>
                        </div>

                        <div
                          className="flex items-center gap-1 flex-shrink-0"
                          onClick={(e) => e.stopPropagation()}
                        >
                          {request.status === 'pending' && (
                            <>
                              <button
                                onClick={() => handleApprove(request.id)}
                                className={`p-2 rounded-xl transition-all ${
                                  isDark
                                    ? 'bg-emerald-500/20 text-emerald-400 hover:bg-emerald-500/30'
                                    : 'bg-emerald-100 text-emerald-600 hover:bg-emerald-200'
                                }`}
                                title={t('timeOff.approve')}
                              >
                                {Icons.check}
                              </button>
                              <button
                                onClick={() => handleReject(request)}
                                className={`p-2 rounded-xl transition-all ${
                                  isDark
                                    ? 'bg-rose-500/20 text-rose-400 hover:bg-rose-500/30'
                                    : 'bg-rose-100 text-rose-600 hover:bg-rose-200'
                                }`}
                                title={t('timeOff.reject')}
                              >
                                {Icons.x}
                              </button>
                            </>
                          )}
                          <button
                            onClick={() => handleDelete(request.id)}
                            className={`p-2 rounded-xl transition-all ${
                              isDark
                                ? 'bg-rose-500/20 text-rose-400 hover:bg-rose-500/30'
                                : 'bg-rose-100 text-rose-600 hover:bg-rose-200'
                            }`}
                            title={t('common.delete')}
                          >
                            {Icons.trash}
                          </button>
                        </div>
                      </div>
                    </button>

                    {/* Expanded Content */}
                    {isExpanded && (
                      <div
                        className={`px-6 pb-6 pt-2 border-t ${isDark ? 'border-slate-700/50' : 'border-slate-100'}`}
                      >
                        <div
                          className={`flex items-center gap-6 text-sm mb-4 ${isDark ? 'text-slate-400' : 'text-slate-600'}`}
                        >
                          <span className="flex items-center gap-1.5">
                            {Icons.mail} {request.user.email}
                          </span>
                          <span className="flex items-center gap-1.5">
                            {Icons.clock}{' '}
                            {request.hours_requested || request.total_days * 8}{' '}
                            {t('timeOff.hours')}
                          </span>
                        </div>

                        <div className="mb-4">
                          <span
                            className={`text-sm font-medium ${isDark ? 'text-slate-300' : 'text-slate-700'}`}
                          >
                            {t('timeOff.reason')}:
                          </span>
                          <p
                            className={`text-sm mt-1 ${isDark ? 'text-white' : 'text-slate-900'}`}
                          >
                            {request.reason}
                          </p>
                        </div>

                        {request.status === 'approved' &&
                          request.approved_by && (
                            <div className="text-sm text-emerald-500 mb-2">
                              {Icons.check} {t('timeOff.approvedBy')}{' '}
                              {request.approved_by.full_name} {t('timeOff.on')}{' '}
                              {new Date(
                                request.approved_at!,
                              ).toLocaleDateString()}
                            </div>
                          )}

                        <div
                          className={`text-xs ${isDark ? 'text-slate-500' : 'text-slate-500'}`}
                        >
                          {t('timeOff.requestedOn')}{' '}
                          {new Date(request.created_at).toLocaleDateString()}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </div>
        )}
      </div>
    </>
  );
};

const NewTimeOffRequest: React.FC = () => {
  const { t } = useTranslation();
  const { theme } = useTheme();
  const { showToast } = useToast();
  const isDark = theme === 'dark';
  const [request, setRequest] = useState({
    request_type: 'vacation',
    start_date: '',
    end_date: '',
    reason: '',
    hours_requested: 0,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  // Theme-aware classes
  const cardClass = isDark
    ? 'bg-slate-900/50 border border-slate-700/50 backdrop-blur-sm'
    : 'bg-white border border-slate-200 shadow-sm';

  const inputClass = isDark
    ? 'w-full px-4 py-2.5 bg-slate-900/50 border border-slate-700 text-white placeholder-slate-500 rounded-xl focus:border-violet-500 focus:ring-1 focus:ring-violet-500 transition-colors'
    : 'w-full px-4 py-2.5 bg-white border border-slate-200 text-slate-900 placeholder-slate-400 rounded-xl focus:border-violet-500 focus:ring-1 focus:ring-violet-500 transition-colors';

  const selectClass = isDark
    ? 'w-full px-4 py-2.5 bg-slate-900/50 border border-slate-700 text-white rounded-xl focus:border-violet-500 focus:ring-1 focus:ring-violet-500 transition-colors'
    : 'w-full px-4 py-2.5 bg-white border border-slate-200 text-slate-900 rounded-xl focus:border-violet-500 focus:ring-1 focus:ring-violet-500 transition-colors';

  const labelClass = isDark
    ? 'block text-sm font-medium text-slate-300 mb-2'
    : 'block text-sm font-medium text-slate-700 mb-2';

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      await timeOffRequestsAPI.create(request);
      showToast(t('timeOff.submitSuccess'), 'success');
      navigate('/manager/time-off');
    } catch (error: any) {
      setError(error.response?.data?.error || t('timeOff.submitFailed'));
    } finally {
      setLoading(false);
    }
  };

  const calculateDays = () => {
    if (request.start_date && request.end_date) {
      const start = new Date(request.start_date);
      const end = new Date(request.end_date);
      const diffTime = Math.abs(end.getTime() - start.getTime());
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
      return diffDays;
    }
    return 0;
  };

  const handleDateRangeChange = (startDate: string, endDate: string) => {
    const calculatedHours = calculateHours(startDate, endDate);
    setRequest({
      ...request,
      start_date: startDate,
      end_date: endDate,
      hours_requested: calculatedHours,
    });
  };

  const calculateHours = (startDate: string, endDate: string) => {
    if (!startDate || !endDate) return 0;
    const start = new Date(startDate);
    const end = new Date(endDate);
    const diffTime = Math.abs(end.getTime() - start.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
    return diffDays * 8;
  };

  return (
    <div
      className={`min-h-screen p-6 ${isDark ? 'bg-slate-900' : 'bg-slate-50'}`}
    >
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <button
            onClick={() => navigate('/manager/time-off')}
            className={`p-2 rounded-xl transition-all ${isDark ? 'text-slate-400 hover:text-white hover:bg-slate-700' : 'text-slate-500 hover:text-slate-900 hover:bg-slate-100'}`}
          >
            {Icons.arrowLeft}
          </button>
          <h1
            className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}
          >
            {t('timeOff.requestTimeOff')}
          </h1>
        </div>

        {error && (
          <div className="mb-6 p-4 bg-rose-500/10 border border-rose-500/20 text-rose-400 rounded-xl">
            {error}
          </div>
        )}

        <form
          onSubmit={handleSubmit}
          className={`${cardClass} rounded-2xl p-6 space-y-6`}
        >
          <div>
            <label className={labelClass}>{t('timeOff.typeOfLeave')} *</label>
            <select
              required
              value={request.request_type}
              onChange={(e) =>
                setRequest({ ...request, request_type: e.target.value })
              }
              className={selectClass}
            >
              <option value="vacation">{t('timeOff.vacation')}</option>
              <option value="sick">{t('timeOff.sickLeave')}</option>
              <option value="personal">{t('timeOff.personal')}</option>
              <option value="emergency">{t('timeOff.emergency')}</option>
            </select>
          </div>

          <div>
            <label className={labelClass}>{t('timeOff.dateRange')} *</label>
            <div className="space-y-3">
              <DateRangePicker
                startDate={request.start_date}
                endDate={request.end_date}
                onDateChange={handleDateRangeChange}
                isDark={isDark}
              />

              {request.start_date && request.end_date && (
                <div
                  className={`text-sm p-3 rounded-xl ${isDark ? 'bg-cyan-500/10 text-cyan-400 border border-cyan-500/20' : 'bg-cyan-50 text-cyan-700 border border-cyan-200'}`}
                >
                  {t('timeOff.duration')}: {calculateDays()}{' '}
                  {calculateDays() !== 1 ? t('timeOff.days') : t('timeOff.day')}{' '}
                  ({request.hours_requested} {t('timeOff.hours')})
                  {request.start_date !== request.end_date && (
                    <span>
                      {' '}
                      • {new Date(request.start_date).toLocaleDateString()}{' '}
                      {t('timeOff.to')}{' '}
                      {new Date(request.end_date).toLocaleDateString()}
                    </span>
                  )}
                </div>
              )}
            </div>
          </div>

          <div>
            <label className={labelClass}>{t('timeOff.reason')} *</label>
            <textarea
              rows={4}
              required
              value={request.reason}
              onChange={(e) =>
                setRequest({ ...request, reason: e.target.value })
              }
              className={inputClass}
              placeholder={t('timeOff.reasonPlaceholder')}
            />
          </div>

          <div
            className={`flex justify-end gap-3 pt-6 border-t ${isDark ? 'border-slate-700' : 'border-slate-200'}`}
          >
            <button
              type="button"
              onClick={() => navigate('/manager/time-off')}
              className={`px-5 py-2.5 font-medium rounded-xl transition-colors ${
                isDark
                  ? 'bg-slate-700 hover:bg-slate-600 text-white'
                  : 'bg-slate-100 hover:bg-slate-200 text-slate-700'
              }`}
            >
              {t('common.cancel')}
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-5 py-2.5 bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-500 hover:to-purple-500 text-white font-semibold rounded-xl shadow-lg shadow-violet-500/25 transition-all duration-200 disabled:opacity-50"
            >
              {loading ? t('timeOff.submitting') : t('timeOff.submitRequest')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const EditTimeOffRequest: React.FC = () => {
  const { t } = useTranslation();
  const { theme } = useTheme();
  const { showToast } = useToast();
  const isDark = theme === 'dark';
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [request, setRequest] = useState({
    request_type: 'vacation',
    start_date: '',
    end_date: '',
    reason: '',
    hours_requested: 0,
    status: 'pending',
  });
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(true);
  const [error, setError] = useState('');

  const cardClass = isDark
    ? 'bg-slate-900/50 border border-slate-700/50 backdrop-blur-sm'
    : 'bg-white border border-slate-200 shadow-sm';

  const inputClass = isDark
    ? 'w-full px-4 py-2.5 bg-slate-900/50 border border-slate-700 text-white placeholder-slate-500 rounded-xl focus:border-violet-500 focus:ring-1 focus:ring-violet-500 transition-colors'
    : 'w-full px-4 py-2.5 bg-white border border-slate-200 text-slate-900 placeholder-slate-400 rounded-xl focus:border-violet-500 focus:ring-1 focus:ring-violet-500 transition-colors';

  const selectClass = isDark
    ? 'w-full px-4 py-2.5 bg-slate-900/50 border border-slate-700 text-white rounded-xl focus:border-violet-500 focus:ring-1 focus:ring-violet-500 transition-colors'
    : 'w-full px-4 py-2.5 bg-white border border-slate-200 text-slate-900 rounded-xl focus:border-violet-500 focus:ring-1 focus:ring-violet-500 transition-colors';

  const labelClass = isDark
    ? 'block text-sm font-medium text-slate-300 mb-2'
    : 'block text-sm font-medium text-slate-700 mb-2';

  useEffect(() => {
    const fetchRequest = async () => {
      try {
        const response = await timeOffRequestsAPI.getById(Number(id));
        const data = response.data.time_off_request;
        setRequest({
          request_type: data.request_type,
          start_date: data.start_date,
          end_date: data.end_date,
          reason: data.reason,
          hours_requested: data.hours_requested || 0,
          status: data.status || 'pending',
        });
      } catch (error) {
        console.error('Failed to fetch request:', error);
        setError(t('timeOff.loadFailed'));
      } finally {
        setFetching(false);
      }
    };
    if (id) fetchRequest();
  }, [id, t]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      await timeOffRequestsAPI.update(Number(id), request);
      showToast(t('timeOff.updateSuccess'), 'success');
      navigate('/manager/time-off');
    } catch (error: any) {
      setError(error.response?.data?.error || t('timeOff.updateFailed'));
    } finally {
      setLoading(false);
    }
  };

  const calculateDays = () => {
    if (request.start_date && request.end_date) {
      const start = new Date(request.start_date);
      const end = new Date(request.end_date);
      const diffTime = Math.abs(end.getTime() - start.getTime());
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
      return diffDays;
    }
    return 0;
  };

  const handleDateRangeChange = (startDate: string, endDate: string) => {
    const calculatedHours = calculateHours(startDate, endDate);
    setRequest({
      ...request,
      start_date: startDate,
      end_date: endDate,
      hours_requested: calculatedHours,
    });
  };

  const calculateHours = (startDate: string, endDate: string) => {
    if (!startDate || !endDate) return 0;
    const start = new Date(startDate);
    const end = new Date(endDate);
    const diffTime = Math.abs(end.getTime() - start.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
    return diffDays * 8;
  };

  if (fetching) {
    return (
      <div
        className={`min-h-screen p-6 ${isDark ? 'bg-slate-900' : 'bg-slate-50'}`}
      >
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-2 border-violet-500 border-t-transparent rounded-full animate-spin" />
        </div>
      </div>
    );
  }

  return (
    <div
      className={`min-h-screen p-6 ${isDark ? 'bg-slate-900' : 'bg-slate-50'}`}
    >
      <div className="max-w-2xl mx-auto">
        <div className="flex items-center gap-4 mb-8">
          <button
            onClick={() => navigate('/manager/time-off')}
            className={`p-2 rounded-xl transition-all ${isDark ? 'text-slate-400 hover:text-white hover:bg-slate-700' : 'text-slate-500 hover:text-slate-900 hover:bg-slate-100'}`}
          >
            {Icons.arrowLeft}
          </button>
          <h1
            className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}
          >
            {t('timeOff.editRequest')}
          </h1>
        </div>

        {error && (
          <div className="mb-6 p-4 bg-rose-500/10 border border-rose-500/20 text-rose-400 rounded-xl">
            {error}
          </div>
        )}

        {request.status !== 'pending' && (
          <div
            className={`mb-6 p-4 rounded-xl ${isDark ? 'bg-amber-500/10 border border-amber-500/20 text-amber-400' : 'bg-amber-50 border border-amber-200 text-amber-700'}`}
          >
            {t('timeOff.cannotEditProcessed')}
          </div>
        )}

        <form
          onSubmit={handleSubmit}
          className={`${cardClass} rounded-2xl p-6 space-y-6`}
        >
          <div>
            <label className={labelClass}>{t('timeOff.typeOfLeave')} *</label>
            <select
              required
              value={request.request_type}
              onChange={(e) =>
                setRequest({ ...request, request_type: e.target.value })
              }
              className={selectClass}
            >
              <option value="vacation">{t('timeOff.vacation')}</option>
              <option value="sick_leave">{t('timeOff.sickLeave')}</option>
              <option value="personal">{t('timeOff.personal')}</option>
              <option value="emergency">{t('timeOff.emergency')}</option>
            </select>
          </div>

          <div>
            <label className={labelClass}>{t('timeEntry.status')} *</label>
            <select
              required
              value={request.status}
              onChange={(e) =>
                setRequest({ ...request, status: e.target.value })
              }
              className={selectClass}
            >
              <option value="pending">{t('timeEntry.pending')}</option>
              <option value="approved">{t('timeEntry.approved')}</option>
              <option value="rejected">{t('timeEntry.rejected')}</option>
            </select>
          </div>

          <div>
            <label className={labelClass}>{t('timeOff.dateRange')} *</label>
            <div className="space-y-3">
              <DateRangePicker
                startDate={request.start_date}
                endDate={request.end_date}
                onDateChange={handleDateRangeChange}
                isDark={isDark}
              />

              {request.start_date && request.end_date && (
                <div
                  className={`text-sm p-3 rounded-xl ${isDark ? 'bg-cyan-500/10 text-cyan-400 border border-cyan-500/20' : 'bg-cyan-50 text-cyan-700 border border-cyan-200'}`}
                >
                  {t('timeOff.duration')}: {calculateDays()}{' '}
                  {calculateDays() !== 1 ? t('timeOff.days') : t('timeOff.day')}{' '}
                  ({request.hours_requested} {t('timeOff.hours')})
                </div>
              )}
            </div>
          </div>

          <div>
            <label className={labelClass}>{t('timeOff.reason')} *</label>
            <textarea
              rows={4}
              required
              value={request.reason}
              onChange={(e) =>
                setRequest({ ...request, reason: e.target.value })
              }
              className={inputClass}
              placeholder={t('timeOff.reasonPlaceholder')}
            />
          </div>

          <div
            className={`flex justify-end gap-3 pt-6 border-t ${isDark ? 'border-slate-700' : 'border-slate-200'}`}
          >
            <button
              type="button"
              onClick={() => navigate('/manager/time-off')}
              className={`px-5 py-2.5 font-medium rounded-xl transition-colors ${
                isDark
                  ? 'bg-slate-700 hover:bg-slate-600 text-white'
                  : 'bg-slate-100 hover:bg-slate-200 text-slate-700'
              }`}
            >
              {t('common.cancel')}
            </button>
            <button
              type="submit"
              disabled={loading || request.status !== 'pending'}
              className="px-5 py-2.5 bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-500 hover:to-purple-500 text-white font-semibold rounded-xl shadow-lg shadow-violet-500/25 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? t('common.saving') : t('common.save')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

interface EmployeeBalance {
  user: { full_name: string };
  balances: {
    leave_type: string;
    used_days: number;
    pending_days: number;
    remaining_days: number;
  }[];
}

const TimeOffReport: React.FC = () => {
  const { t } = useTranslation();
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const currentYear = new Date().getFullYear();
  const [reportYear, setReportYear] = useState(currentYear);
  const [data, setData] = useState<EmployeeBalance[]>([]);
  const [loading, setLoading] = useState(true);

  const cardClass = isDark
    ? 'bg-slate-900/50 border border-slate-700/50 backdrop-blur-sm'
    : 'bg-white border border-slate-200 shadow-sm';

  const selectClass = isDark
    ? 'px-4 py-2.5 bg-slate-900/50 border border-slate-700 text-white rounded-xl focus:border-violet-500 focus:ring-1 focus:ring-violet-500 transition-colors'
    : 'px-4 py-2.5 bg-white border border-slate-200 text-slate-900 rounded-xl focus:border-violet-500 focus:ring-1 focus:ring-violet-500 transition-colors';

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const response = await timeOffBalancesAPI.teamSummary(reportYear);
        const employees: EmployeeBalance[] =
          response.data.employees || response.data || [];
        const sorted = [...employees].sort((a, b) =>
          a.user.full_name.localeCompare(b.user.full_name),
        );
        setData(sorted);
      } catch (error) {
        console.error('Failed to fetch team summary:', error);
        setData([]);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [reportYear]);

  const getBalance = (emp: EmployeeBalance, type: string) => {
    const b = emp.balances.find((b) => b.leave_type === type);
    return {
      used: b?.used_days ?? 0,
      pending: b?.pending_days ?? 0,
      remaining: b?.remaining_days ?? 0,
    };
  };

  const exportToCSV = () => {
    const headers = [
      'Employee',
      'Vacation Used',
      'Vacation Pending',
      'Vacation Remaining',
      'Sick Used',
      'Sick Pending',
      'Sick Remaining',
      'Personal Used',
      'Personal Pending',
      'Personal Remaining',
    ];
    const rows = data.map((emp) => {
      const vac = getBalance(emp, 'vacation');
      const sick = getBalance(emp, 'sick_leave');
      const per = getBalance(emp, 'personal');
      return [
        emp.user.full_name,
        vac.used,
        vac.pending,
        vac.remaining,
        sick.used,
        sick.pending,
        sick.remaining,
        per.used,
        per.pending,
        per.remaining,
      ];
    });
    const csv = [headers, ...rows].map((row) => row.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `time-off-report-${reportYear}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const thClass = `px-3 py-3 text-xs font-semibold uppercase tracking-wider ${isDark ? 'text-slate-400' : 'text-slate-500'}`;
  const tdClass = `px-3 py-3 text-sm ${isDark ? 'text-slate-300' : 'text-slate-700'}`;
  const tdNumClass = `px-3 py-3 text-sm text-center ${isDark ? 'text-slate-300' : 'text-slate-700'}`;

  return (
    <div
      className={`min-h-screen p-6 ${isDark ? 'bg-slate-900' : 'bg-slate-50'}`}
    >
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 mb-6">
        <div>
          <div className="flex items-center gap-3 mb-1">
            <div
              className={`hidden sm:flex p-2 rounded-xl ${isDark ? 'bg-violet-500/20' : 'bg-violet-100'}`}
            >
              <span className={isDark ? 'text-violet-400' : 'text-violet-600'}>
                {Icons.calendar}
              </span>
            </div>
            <h1
              className={`text-xl sm:text-2xl font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}
            >
              {t('timeOff.reportTitle', 'Time-Off Report')}
            </h1>
          </div>
          <p
            className={`text-sm ${isDark ? 'text-slate-400' : 'text-slate-600'} sm:ml-14`}
          >
            {t('timeOff.reportSubtitle', 'Balance summary by employee')}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <select
            value={reportYear}
            onChange={(e) => setReportYear(Number(e.target.value))}
            className={selectClass}
          >
            <option value={currentYear - 1}>{currentYear - 1}</option>
            <option value={currentYear}>{currentYear}</option>
          </select>
          <button
            onClick={exportToCSV}
            disabled={data.length === 0}
            className="inline-flex items-center gap-2 px-4 py-2.5 bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-500 hover:to-purple-500 disabled:opacity-50 text-white text-sm font-semibold rounded-xl shadow-lg shadow-violet-500/25 transition-all duration-200"
          >
            {Icons.arrowLeft}
            {t('timeOff.exportCSV', 'Export CSV')}
          </button>
        </div>
      </div>

      {/* Table */}
      <div className={`${cardClass} rounded-2xl overflow-hidden`}>
        {loading ? (
          <div className="animate-pulse p-6 space-y-3">
            {[1, 2, 3, 4].map((i) => (
              <div
                key={i}
                className={`h-10 rounded-xl ${isDark ? 'bg-slate-700' : 'bg-slate-200'}`}
              />
            ))}
          </div>
        ) : data.length === 0 ? (
          <div
            className={`p-12 text-center ${isDark ? 'text-slate-400' : 'text-slate-500'}`}
          >
            <div
              className={`w-16 h-16 mx-auto mb-4 rounded-2xl ${isDark ? 'bg-slate-800' : 'bg-slate-100'} flex items-center justify-center`}
            >
              <span className={isDark ? 'text-slate-600' : 'text-slate-400'}>
                {Icons.calendar}
              </span>
            </div>
            <p className="font-medium">
              {t('timeOff.noData', 'No balance data available')}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr
                  className={`border-b ${isDark ? 'border-slate-700/50' : 'border-slate-100'}`}
                >
                  <th className={`${thClass} text-left`}>
                    {t('timeOff.employee', 'Employee')}
                  </th>
                  <th className={`${thClass} text-center`} colSpan={3}>
                    {t('timeOff.vacation', 'Vacation')}
                  </th>
                  <th className={`${thClass} text-center`} colSpan={3}>
                    {t('timeOff.sickLeave', 'Sick')}
                  </th>
                  <th className={`${thClass} text-center`} colSpan={3}>
                    {t('timeOff.personal', 'Personal')}
                  </th>
                </tr>
                <tr
                  className={`border-b ${isDark ? 'border-slate-700/50 bg-slate-800/30' : 'border-slate-100 bg-slate-50'}`}
                >
                  <th className={`${thClass} text-left`}></th>
                  {['vacation', 'sick_leave', 'personal'].map((type) => (
                    <React.Fragment key={type}>
                      <th className={thClass}>{t('timeOff.used', 'Used')}</th>
                      <th className={thClass}>
                        {t('timeOff.pending', 'Pending')}
                      </th>
                      <th className={thClass}>
                        {t('timeOff.remaining', 'Remaining')}
                      </th>
                    </React.Fragment>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.map((emp, idx) => {
                  const vac = getBalance(emp, 'vacation');
                  const sick = getBalance(emp, 'sick_leave');
                  const per = getBalance(emp, 'personal');
                  return (
                    <tr
                      key={idx}
                      className={`border-b ${isDark ? 'border-slate-700/30 hover:bg-slate-800/30' : 'border-slate-100 hover:bg-slate-50'} transition-colors`}
                    >
                      <td className={`${tdClass} font-medium`}>
                        {emp.user.full_name}
                      </td>
                      <td className={tdNumClass}>{vac.used}</td>
                      <td className={tdNumClass}>
                        {vac.pending > 0 ? (
                          <span
                            className={`inline-flex px-1.5 py-0.5 text-xs rounded-md ${isDark ? 'bg-amber-500/20 text-amber-400' : 'bg-amber-50 text-amber-700'}`}
                          >
                            {vac.pending}
                          </span>
                        ) : (
                          vac.pending
                        )}
                      </td>
                      <td className={tdNumClass}>
                        <span
                          className={`inline-flex px-1.5 py-0.5 text-xs rounded-md ${isDark ? 'bg-emerald-500/20 text-emerald-400' : 'bg-emerald-50 text-emerald-700'}`}
                        >
                          {vac.remaining}
                        </span>
                      </td>
                      <td className={tdNumClass}>{sick.used}</td>
                      <td className={tdNumClass}>
                        {sick.pending > 0 ? (
                          <span
                            className={`inline-flex px-1.5 py-0.5 text-xs rounded-md ${isDark ? 'bg-amber-500/20 text-amber-400' : 'bg-amber-50 text-amber-700'}`}
                          >
                            {sick.pending}
                          </span>
                        ) : (
                          sick.pending
                        )}
                      </td>
                      <td className={tdNumClass}>
                        <span
                          className={`inline-flex px-1.5 py-0.5 text-xs rounded-md ${isDark ? 'bg-emerald-500/20 text-emerald-400' : 'bg-emerald-50 text-emerald-700'}`}
                        >
                          {sick.remaining}
                        </span>
                      </td>
                      <td className={tdNumClass}>{per.used}</td>
                      <td className={tdNumClass}>
                        {per.pending > 0 ? (
                          <span
                            className={`inline-flex px-1.5 py-0.5 text-xs rounded-md ${isDark ? 'bg-amber-500/20 text-amber-400' : 'bg-amber-50 text-amber-700'}`}
                          >
                            {per.pending}
                          </span>
                        ) : (
                          per.pending
                        )}
                      </td>
                      <td className={tdNumClass}>
                        <span
                          className={`inline-flex px-1.5 py-0.5 text-xs rounded-md ${isDark ? 'bg-emerald-500/20 text-emerald-400' : 'bg-emerald-50 text-emerald-700'}`}
                        >
                          {per.remaining}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

const TimeOffManagement: React.FC = () => {
  return (
    <Routes>
      <Route index element={<TimeOffList />} />
      <Route path="new" element={<NewTimeOffRequest />} />
      <Route path=":id/edit" element={<EditTimeOffRequest />} />
      <Route path="report" element={<TimeOffReport />} />
    </Routes>
  );
};

export default TimeOffManagement;
